import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { ReasonModel } from 'models/generated/Entities.GormCrm.Reason';
import { ReasonCategoryModel } from 'models/generated/Entities.GormCrm.ReasonCategory';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ReasonService {

	private baseUrl = `${environment.gatewayUrl}/api/reasons`;
	private cachedReasonRequests = new Map<boolean, Promise<ReasonModel[]>>();

	constructor(private readonly http: HttpClient) { }

	async getReason(reasonId: string): Promise<ReasonModel | undefined> {
		const reasons = await this.getCachedReasonsList(true);
		return reasons.find(r => r.id == reasonId);
	}
	
	private getCachedReasonsList(includeDeleted: boolean): Promise<ReasonModel[]> {
		// Return cached request if cache exists
		if (this.cachedReasonRequests.has(includeDeleted)) return this.cachedReasonRequests.get(includeDeleted)!;
		return this.getAllReasons(includeDeleted);
	}

	getAllReasons(includeDeleted = false): Promise<ReasonModel[]> {
		// Force update of cached values, to ensure it's always up-to-date while editing the reason list
		const request = firstValueFrom(this.http.get<ReasonModel[]>(`${this.baseUrl}?includeDeleted=${includeDeleted}`));
		this.cachedReasonRequests.set(includeDeleted, request);
		return request;
	}

	getReasonMap() {
		return this.getAllReasons(true).then(reasons => {
			const map = new Map<string, ReasonModel>();
			reasons.forEach(reason => map.set(reason.id!, reason));
			return map;
		});
	}

	getAllReasonCategories(): Promise<ReasonCategoryModel[]> {
		return firstValueFrom(this.http.get<ReasonCategoryModel[]>(`${this.baseUrl}/categories`));
	}

	saveReason(reason: ReasonModel): Promise<ReasonModel> {
		if (!reason.id) return firstValueFrom(this.http.post<ReasonModel>(this.baseUrl, reason));
		return firstValueFrom(this.http.put<ReasonModel>(`${this.baseUrl}/${reason.id}`, reason));
	}

	saveReasonCategory(category: ReasonCategoryModel): Promise<ReasonCategoryModel> {
		if (!category.id) return firstValueFrom(this.http.post<ReasonCategoryModel>(`${this.baseUrl}/categories`, category));
		return firstValueFrom(this.http.put<ReasonCategoryModel>(`${this.baseUrl}/categories/${category.id}`, category));
	}

	deleteReason(reasonId: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/${reasonId}`));
	}

	deleteReasonCategory(reasonCategoryId: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.baseUrl}/categories/${reasonCategoryId}`));
	}
}
